import {
  ANALYST_FUNCTION_NAMES,
  ANALYST_LEVEL_LABELS,
  type AnalystFunction,
  type AnalystLevel,
} from '../schemas/analyst.js';
import type { NoyauCode, NoyauScore } from '../schemas/noyau.js';
import type { LevelReading } from './levels.js';

/**
 * THE SIX NOTES, derived (analyste.md §8, issue #196): from the compatibility the
 * code computed and the style the second pass noted, with the weight of the
 * style the protocol's section sets. These are the six notes the rest of the
 * product already reads (`noyau.md` §4.2) - what changes is that the model no
 * longer gives them, the code derives them.
 *
 * ## A candidate and an eliminated are not noted the same way
 *
 * A CANDIDATE mixes its compatibility and its style (§8.1). An ELIMINATED keeps
 * its compatibility, capped under the lowest candidate (§8.2, K8), and the
 * sentence of its note is written by the code (§8.4, K9): the second pass never
 * saw it.
 */

/** The sentence of a candidate whose style could not be read: two failed calls, the notes on the compatibility alone (§7.5, §8.4). */
export const STYLE_ABSENT_SENTENCE = 'Compatibilité seule : le style n’a pas pu être lu.';

/**
 * The sentence of an eliminated noyau (§8.4, K9), written by the code: it names
 * the FIRST fixed level it contradicts, « Éliminé : le Pourquoi est fixé
 * Guerrier. ».
 */
export function eliminatedSentence(level: AnalystLevel, fn: AnalystFunction): string {
  return `Éliminé : le ${ANALYST_LEVEL_LABELS[level]} est fixé ${ANALYST_FUNCTION_NAMES[fn]}.`;
}

/**
 * The NOTE of a candidate (§8.1): `(100 - weight) / 100 * compatibility +
 * weight / 100 * style`, rounded to the nearest, a half upwards. With the style
 * absent - `null` - it is the compatibility. The arithmetic is on integers, so
 * 72.5 is 73 on every machine.
 */
export function candidateNote(
  compatibility: number,
  style: number | null,
  styleWeight: number,
): number {
  if (style === null) return compatibility;
  return Math.floor((compatibility * (100 - styleWeight) + style * styleWeight + 50) / 100);
}

/**
 * The NOTE of an eliminated noyau (§8.2, K8): its compatibility, capped under the
 * last candidate - at most the lowest note of the candidates minus one - and never
 * below zero. With no candidate to cap it - which the engine never asks - it keeps
 * its compatibility.
 */
export function eliminatedNote(compatibility: number, lowestCandidateNote: number | null): number {
  if (lowestCandidateNote === null) return compatibility;
  return Math.max(0, Math.min(compatibility, lowestCandidateNote - 1));
}

/** One note of the second pass, once its passages are checked: the style of a candidate, and its sentence. */
export type StyleScore = {
  readonly noyau: NoyauCode;
  readonly score: number;
  readonly justification: string;
};

/**
 * One of the six noyaux, noted: whether it is a candidate, the first level that
 * eliminated it, its compatibility, its style (null for an eliminated, and for a
 * candidate whose style is absent), its note and the sentence of its note.
 */
export type AnalyzedNoyau = {
  readonly noyau: NoyauCode;
  readonly candidate: boolean;
  readonly eliminatedBy: AnalystLevel | null;
  readonly compatibility: number;
  readonly style: number | null;
  readonly score: number;
  readonly justification: string;
};

/**
 * NOTE the six noyaux, in the order of the table (§8): the candidates from their
 * compatibility and their style, the eliminated from their compatibility and the
 * lowest candidate.
 *
 * `styles` is the second pass after the check of its passages, or `null` when it
 * failed twice - « style absent » (§7.5): every candidate is then noted on its
 * compatibility alone, with `STYLE_ABSENT_SENTENCE`. A candidate the second pass
 * left out - which its validation forbids - is noted the same way.
 */
export function analyzeNoyaux(input: {
  readonly reading: LevelReading;
  readonly styles: readonly StyleScore[] | null;
  readonly styleWeight: number;
}): AnalyzedNoyau[] {
  const { reading, styles, styleWeight } = input;
  const candidates = new Set(reading.candidates);
  const styleOf = new Map((styles ?? []).map((entry) => [entry.noyau, entry]));
  const eliminationOf = new Map(reading.eliminated.map((entry) => [entry.noyau, entry]));

  const candidateRows = reading.compatibility.flatMap(({ noyau, compatibility }) => {
    if (!candidates.has(noyau)) return [];
    const style = styleOf.get(noyau) ?? null;
    return [
      {
        noyau,
        compatibility,
        style: style?.score ?? null,
        score: candidateNote(compatibility, style?.score ?? null, styleWeight),
        justification: style?.justification ?? STYLE_ABSENT_SENTENCE,
      },
    ];
  });
  const lowest =
    candidateRows.length === 0 ? null : Math.min(...candidateRows.map((row) => row.score));
  const candidateOf = new Map(candidateRows.map((row) => [row.noyau, row]));

  return reading.compatibility.map(({ noyau, compatibility }): AnalyzedNoyau => {
    const candidate = candidateOf.get(noyau);
    if (candidate !== undefined) {
      return { ...candidate, candidate: true, eliminatedBy: null };
    }
    const elimination = eliminationOf.get(noyau);
    // `candidates` and `eliminated` come together from `readLevels`: a noyau that
    // is in neither is a reading assembled by hand, and no note is honest for it.
    if (elimination === undefined) {
      throw new Error(`${noyau} is neither a candidate nor an eliminated noyau of the reading.`);
    }
    return {
      noyau,
      candidate: false,
      eliminatedBy: elimination.level,
      compatibility,
      style: null,
      score: eliminatedNote(compatibility, lowest),
      justification: eliminatedSentence(elimination.level, elimination.function),
    };
  });
}

/** The notes as the determination stores them: `{ noyau, score, justification }`, one per noyau. */
export function noyauScoresOf(rows: readonly AnalyzedNoyau[]): NoyauScore[] {
  return rows.map((row) => ({
    noyau: row.noyau,
    score: row.score,
    justification: row.justification,
  }));
}
